import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TypeOfRequestLabel, TypeOfOperation } from '@base/enums/request-label.enum';
import { ReqLabelParams } from '@base/models/requests/req-label.req';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { GlobalService } from '@base/services/private/global.service';
import { Subscription } from 'rxjs';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { ThemeService } from '@base/services/private/theme.service';

@Component({
  selector: 'app-modal-unarchive',
  imports: [ModalHeaderComponent, ModalBodyComponent, forwardRef(() => TranslatePipe), ModalFooterComponent],
  templateUrl: './modal-unarchive.component.html',
  styleUrl: './modal-unarchive.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalUnarchiveComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly globalSrv = inject(GlobalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private httpRequestObserver$: Subscription | null = null;
  private readonly themeSrv = inject(ThemeService);

  @Input() peopleId = '';
  @Input() isCloseAll = false;
  @Input() modalId = '';

  isHttpRequestRunning = false;
  isBgDarkMode = this.themeSrv.isBgDarkMode;

  ngOnInit(): void {
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Performs the unarchive action for the given peopleId and updates the label.
   * Closes either all modals or just the current one.
   * @returns void
   */
  doUnarchive(): void {
    const data: ReqLabelParams = {
      id_object: this.peopleId,
      action: TypeOfRequestLabel.ATTENDEE_ARCHIVE,
      operation: TypeOfOperation.NEGATIVE,
      reason: '',
    };
    this.requestLabelSrv.updateLabel(data).subscribe({
      next: (res) => {
        if (res.status === StatusResponse.TEXT_SUCCESS) {
          const data = {
            status: true,
            data: {
              id: this.peopleId!,
              action: TypeOfRequestLabel.ATTENDEE_ARCHIVE,
            },
          };
          this.requestLabelSrv.setEventUpdated(data);
          this.close();
        } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
          // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
          this.showModalWarning();
        }
      },
      error: (err) => {
        console.error('Error unarchiving:', err);
      },
    });
  }

  /**
   * Closes the current modal by its ID.
   * @returns void
   */
  close(): void {
    this.modalSrv.close();
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
